import React, { useCallback, useMemo, useState } from "react";
import ModalWrapper from "..";
import { Button } from "../../button";
import { useTranslation } from "react-i18next";
import { formattedDateTimeWithoutSec } from "../../../lib/functions";
import { useReactToPrint, UseReactToPrintOptions } from "react-to-print";
import PrintableBadgeCard from "../../card/PrintableBadgeCard";
import { usePrintBadgeForVisitor } from "../../../hooks/visitor/mutation";
import toast from "react-hot-toast";
import CheckboxInput from "../../inputs/CheckboxInput";

interface PrintBadgeModalProps {
  appointment: any;
  isOpen: boolean;
  onOpenChange: () => void;
}

const PrintBadgeModal = ({
  appointment,
  isOpen,
  onOpenChange,
}: PrintBadgeModalProps) => {
  const componentRef = React.useRef(null);

  const { t, i18n } = useTranslation();

  const [isPrintBadgeWithoutPicture, setIsPrintBadgeWithoutPicture] =
    useState<boolean>(false);

  const { mutateAsync: PrintBadgeForVisitor } = usePrintBadgeForVisitor();

  const visitorPhoto = useMemo(
    () => `data:image/png;base64,${appointment?.s_Photo}`,
    [appointment?.s_Photo]
  );

  const visitorInfo = useMemo(
    () => [
      {
        id: 1,
        titleEn: "Reference Number",
        titleAr: "الرقم المرجعي",
        value: appointment?.s_AppointmentCode,
      },
      {
        id: 2,
        titleEn: "Visitor Name",
        titleAr: "اسم الزائر",
        value: appointment?.s_VisitorName,
      },
      {
        id: 3,
        titleEn: "Organization Name",
        titleAr: "الجهة التابع لها",
        value: appointment?.s_Sponsor,
      },
      {
        id: 8,
        titleEn: "Building",
        titleAr: "مبنى",
        value:
          i18n.language === "en"
            ? appointment?.s_BuildingName_En
            : appointment?.s_BuildingName_Ar,
      },
      {
        id: 4,
        titleEn: "Department",
        titleAr: "قسم",
        value:
          i18n.language === "en"
            ? appointment?.s_DepartmentName_En
            : appointment?.s_DepartmentName_Ar,
      },
      {
        id: 5,
        titleEn: "Issue and Expiry Date",
        titleAr: "تاريخ الإصدار والانتهاء",
        value: `${formattedDateTimeWithoutSec(
          appointment?.dt_AppointmentStartTime
        )} - ${formattedDateTimeWithoutSec(
          appointment?.dt_AppointmentEndTime
        )}`,
        styles: "font-bold text-red-600 badge-dates",
      },
      {
        id: 5,
        titleEn: "Host Name",
        titleAr: "اسم المضيف",
        value: appointment?.s_HostName,
        styles: "badge-dates"
      },
      {
        id: 6,
        titleEn: "Host Number",
        titleAr: "رقم المضيف",
        value: appointment?.s_HostMobileNo,
      },
      {
        id: 7,
        titleEn: "Devices",
        titleAr: "الأجهزة",
        value:
          i18n.language === "en"
            ? appointment?.s_AllowedDevices_En
            : appointment?.s_AllowedDevices_Ar,
      },
    ],
    [
      appointment?.dt_AppointmentEndTime,
      appointment?.dt_AppointmentStartTime,
      appointment?.s_AllowedDevices_Ar,
      appointment?.s_AllowedDevices_En,
      appointment?.s_AppointmentCode,
      appointment?.s_BuildingName_Ar,
      appointment?.s_BuildingName_En,
      appointment?.s_DepartmentName_Ar,
      appointment?.s_DepartmentName_En,
      appointment?.s_HostMobileNo,
      appointment?.s_HostName,
      appointment?.s_Sponsor,
      appointment?.s_VisitorName,
      i18n.language,
    ]
  );

  const onClose = useCallback(() => {
    onOpenChange();
    setIsPrintBadgeWithoutPicture(false);
  }, [onOpenChange]);

  const handleOnAfterPrint = React.useCallback(async () => {
    try {
      const body = {
        encryptedId: appointment?.n_AppointmentID
          ? appointment?.n_AppointmentID?.toString()
          : "",
      };
      await PrintBadgeForVisitor(body);
    } catch (error) {
      toast.error(t("somethingWentWrong"));
    } finally {
      onClose();
    }
  }, [PrintBadgeForVisitor, appointment?.n_AppointmentID, onClose, t]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    contentRef: componentRef,
    documentTitle: "Visitor Badge",
    onAfterPrint: handleOnAfterPrint,
    removeAfterPrint: true,
  } as UseReactToPrintOptions);

  return (
    <ModalWrapper isOpen={isOpen} onOpenChange={onClose}>
      <div className="flex flex-col gap-5 justify-center items-center">
        <div className="flex flex-col gap-4 justify-center items-center">
          <p className="text-lg md:text-2xl font-medium">
            {t("areYouSureYouWantToPrintBadgeForThisVisitor")}
          </p>
          <CheckboxInput
            key="print-badge-without-picture"
            id="print-badge-without-picture"
            label={t("printBadgeWithoutPicture")}
            checked={isPrintBadgeWithoutPicture}
            setChecked={() => setIsPrintBadgeWithoutPicture((prev) => !prev)}
          />
        </div>

        <div className="hidden print:block">
          <PrintableBadgeCard
            ref={componentRef}
            visitorInfo={visitorInfo}
            visitorPhoto={!isPrintBadgeWithoutPicture ? visitorPhoto : ""}
            qrCode={appointment?.s_QRCodeValue}
          />
        </div>

        <div className="flex gap-1 items-center">
          <Button onClick={handlePrint}>{t("print")}</Button>
          <Button
            className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            onClick={onClose}
          >
            {t("close")}
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default React.memo(PrintBadgeModal);
